import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { CheckCircle2, Copy, Download, Eye, MoreHorizontal, Search, Trash2 } from "lucide-react"
import { licenceStatus, type Licence } from "@xop/dashboard-shared"

import { BulkActionsBar } from "@/components/ui/bulk-actions-bar"
import { BulkDeleteDialog } from "@/components/ui/bulk-delete-dialog"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { Pagination } from "@/components/ui/pagination"
import { saveBlob } from "@/lib/download"
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard"
import { ResellerOnly, useResellerAccess } from "@crm-src/components/reseller"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import type { SortDir } from "@crm-src/components/table/table-query"
import { useTableSelection } from "@crm-src/components/table/useTableSelection"
import usePermissions from "@crm-src/hooks/usePermissions"

import { CreateLicenceDialog } from "./create-licence-dialog"
import { LicenceRenewalChip, LicenceStatusChip, LicenceWorkflowChip } from "./licence-chips"
import { approveLicence, deleteLicence, downloadLicenceFile, fetchLicences } from "./licences-api"

/**
 * CRM ▸ Licences — every appliance licence in the actor's scope.
 *
 * SCOPE IS NOT DECIDED HERE. crm-api narrows the list through the same
 * `customers_scope` every other CRM read uses, so a sales manager sees the
 * licences of the customers and attached resellers it already sees. This page
 * renders whatever it is given.
 *
 * The whole scope arrives in one read (a few hundred rows at most), so search,
 * sorting and paging are all done here rather than round-tripping — unlike the
 * customers table, whose thousands of rows sort on the server. The default
 * order is the server's: soonest expiry first, perpetual last, because a
 * licence with no end date is not "expiring furthest away", it is a different
 * kind of thing.
 *
 * Row actions live in ONE three-dots menu (the CRM's convention), bulk actions
 * in the shared bar over a checkbox column, and the CTO's yes is a green
 * Approve where the file will be — super admins only, command center only.
 */

type Filter = "all" | "attention" | "awaiting" | "internal"
type SortKey = "customer" | "reseller" | "ports" | "expires" | "term" | "workflow" | "file"

const PER_PAGE = 25

function ports(licence: Licence): string {
  return `${licence.ports.audioConferencing} audio / ${licence.ports.webConferencing} web`
}

function expiryLabel(licence: Licence): string {
  return licence.expiresOn ?? "No end date"
}

/** A reseller-less customer bought DIRECT from XOP; say so rather than "—".
 * One of OUR OWN boxes has no reseller either, and "Direct" would read as a
 * customer who buys from us — say "Internal" instead. */
function resellerLabel(licence: Licence): string {
  if (licence.customerInternal) return "Internal"
  return licence.reseller?.name ?? "Direct"
}

function approvalSummary(licence: Licence): string {
  return `${licence.customerName} — ${licence.label || licence.hardwareAddress}: ${
    licence.expiresOn ? `until ${licence.expiresOn}` : "PERPETUAL"
  }, ${licence.ports.audioConferencing} audio / ${licence.ports.webConferencing} web ports, ${
    licence.features.length
  } features. Approving signs the file now and emails the requester that it is ready to download.`
}

const TERM_ORDER: Record<ReturnType<typeof licenceStatus>, number> = { expired: 0, expiring: 1, active: 2, perpetual: 3 }
const WORKFLOW_ORDER: Record<Licence["approval"]["status"], number> = { pending: 0, rejected: 1, not_required: 2, approved: 3 }

/** One comparator per column. Perpetual sorts LAST under "expires" both ways
 * on purpose — it is a kind, not a date. */
function compare(a: Licence, b: Licence, key: SortKey): number {
  switch (key) {
    case "customer":
      return a.customerName.localeCompare(b.customerName) || a.label.localeCompare(b.label)
    case "reseller":
      return resellerLabel(a).localeCompare(resellerLabel(b))
    case "ports":
      return a.ports.audioConferencing - b.ports.audioConferencing || a.ports.webConferencing - b.ports.webConferencing
    case "expires":
      if (a.expiresOn === null && b.expiresOn === null) return 0
      if (a.expiresOn === null) return 1
      if (b.expiresOn === null) return -1
      return a.expiresOn.localeCompare(b.expiresOn)
    case "term":
      return TERM_ORDER[licenceStatus(a)] - TERM_ORDER[licenceStatus(b)]
    case "workflow":
      return WORKFLOW_ORDER[a.approval.status] - WORKFLOW_ORDER[b.approval.status]
    case "file":
      return Number(Boolean(b.currentIssue)) - Number(Boolean(a.currentIssue))
  }
}

export function sortLicences(rows: Licence[], key: SortKey, dir: SortDir): Licence[] {
  const sign = dir === "asc" ? 1 : -1
  return [...rows].sort((a, b) => {
    if (key === "expires") {
      // The perpetual-last rule survives a direction flip.
      if (a.expiresOn === null && b.expiresOn !== null) return 1
      if (b.expiresOn === null && a.expiresOn !== null) return -1
    }
    return compare(a, b, key) * sign
  })
}

export default function LicencesPage() {
  const navigate = useNavigate()
  const { canApproveLicences } = usePermissions()
  const seesResellers = useResellerAccess()
  const [licences, setLicences] = useState<Licence[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState<Filter>("all")
  const [sort, setSort] = useState<SortKey>("expires")
  const [dir, setDir] = useState<SortDir>("asc")
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ el: HTMLElement; id: number } | null>(null)
  const [approving, setApproving] = useState<Licence[]>([])
  const [deleting, setDeleting] = useState<Licence[]>([])
  const selection = useTableSelection<Licence>(`licences|${search}|${filter}`)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setForbidden(false)
    try {
      setLicences(await fetchLicences())
    } catch (err) {
      // An empty table reads as "this scope holds no licences", which is a
      // different and more alarming fact than "you may not see them".
      const status = (err as { status?: number })?.status
      if (status === 403) setForbidden(true)
      else setError(err instanceof Error ? err.message : "Could not load licences.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const rows = licences.filter((licence) => {
      if (filter === "attention") {
        const status = licenceStatus(licence)
        if (status !== "expired" && status !== "expiring") return false
      }
      if (filter === "awaiting" && licence.approval.status !== "pending") return false
      if (filter === "internal" && !licence.customerInternal) return false
      if (!needle) return true
      return (
        licence.customerName.toLowerCase().includes(needle) ||
        licence.label.toLowerCase().includes(needle) ||
        licence.hardwareAddress.toLowerCase().includes(needle) ||
        resellerLabel(licence).toLowerCase().includes(needle)
      )
    })
    return sortLicences(rows, sort, dir)
  }, [licences, search, filter, sort, dir])

  useEffect(() => {
    setPage(1)
  }, [search, filter, sort, dir])
  const pageRows = useMemo(() => visible.slice((page - 1) * PER_PAGE, page * PER_PAGE), [visible, page])

  const toggleSort = (column: string) => {
    const key = column as SortKey
    if (key === sort) setDir(dir === "asc" ? "desc" : "asc")
    else {
      setSort(key)
      setDir("asc")
    }
  }

  const selectedIds = new Set(selection.selected.map((row) => row.id))
  const allOnPageSelected = pageRows.length > 0 && pageRows.every((row) => selectedIds.has(row.id))
  const toggleRow = (licence: Licence) => {
    selection.setSelected(
      selectedIds.has(licence.id)
        ? selection.selected.filter((row) => row.id !== licence.id)
        : [...selection.selected, licence],
    )
  }
  const togglePage = () => {
    selection.setSelected(
      allOnPageSelected
        ? selection.selected.filter((row) => !pageRows.some((p) => p.id === row.id))
        : [...selection.selected, ...pageRows.filter((row) => !selectedIds.has(row.id))],
    )
  }

  // The file, from the list. saveBlob, never an <a download>: the desktop
  // shell drops that into an opaque default with no save prompt.
  const download = async (licence: Licence) => {
    setBusy(licence.id)
    setActionError(null)
    try {
      const blob = await downloadLicenceFile(licence.id)
      await saveBlob(`${licence.customerName}-${licence.currentIssue?.serial ?? "licence"}.zip`, blob)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not download the licence file.")
    } finally {
      setBusy(null)
    }
  }

  // The CTO's yes — one or many. Each signs in its own call; a refusal on one
  // does not stop the rest, and the row shows what came back.
  const approve = async () => {
    const targets = approving
    setApproving([])
    setActionError(null)
    const failures: string[] = []
    for (const target of targets) {
      setBusy(target.id)
      try {
        const approved = await approveLicence(target.id)
        setLicences((current) => current.map((row) => (row.id === approved.id ? approved : row)))
      } catch (err) {
        failures.push(`${target.customerName} — ${target.label || target.hardwareAddress}: ${err instanceof Error ? err.message : "failed"}`)
      }
    }
    setBusy(null)
    selection.clear()
    if (failures.length > 0) setActionError(`Not approved: ${failures.join("; ")}`)
  }

  const remove = async (ids: string[]) => {
    setActionError(null)
    const failures: string[] = []
    for (const id of ids.map(Number)) {
      try {
        await deleteLicence(id)
        setLicences((current) => current.filter((row) => row.id !== id))
      } catch (err) {
        failures.push(err instanceof Error ? err.message : `#${id}`)
      }
    }
    selection.clear()
    setDeleting([])
    if (failures.length > 0) setActionError(`Not deleted: ${failures.join("; ")}`)
  }

  const pendingSelected = selection.selected.filter((row) => row.approval.status === "pending")
  const menuRow = menu ? pageRows.find((row) => row.id === menu.id) ?? licences.find((row) => row.id === menu.id) : undefined

  return (
    <PagePermissionGuard requiredPermission="licences:access">
      <Box>
        {/* A standalone page since 2026-09-13 — it shared a title and a tab
            strip with Releases for one day. Title and button, no prose: the
            table says what a licence is better than a sentence above it. */}
        <PageHeader
          title="Licences"
          actions={
            <Button variant="contained" onClick={() => setCreating(true)}>
              New licence
            </Button>
          }
        />
        <CreateLicenceDialog open={creating} onClose={() => setCreating(false)} />
        {actionError ? (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        ) : null}

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search customer, reseller, label or hardware address"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filter}
                onChange={(_event, next: Filter | null) => next && setFilter(next)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="attention">Expiring or expired</ToggleButton>
                <ToggleButton value="awaiting">Awaiting approval</ToggleButton>
                <ToggleButton value="internal">Our own appliances</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <BulkActionsBar selectedCount={selection.selected.length} entityNoun="licence" onClear={selection.clear}>
              {canApproveLicences() && pendingSelected.length > 0 ? (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircle2 size={16} />}
                  onClick={() => setApproving(pendingSelected)}
                >
                  Approve {pendingSelected.length}
                </Button>
              ) : null}
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<Trash2 size={16} />}
                onClick={() => setDeleting(selection.selected)}
              >
                Delete {selection.selected.length}
              </Button>
            </BulkActionsBar>

            {loading ? (
              <PageLoader />
            ) : forbidden ? (
              <Typography sx={{ py: 6, textAlign: "center" }} color="text.secondary">
                You do not have access to licences.
              </Typography>
            ) : error ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
                <Button onClick={() => void load()}>Try again</Button>
              </Box>
            ) : visible.length === 0 ? (
              <Typography sx={{ py: 6, textAlign: "center" }} color="text.secondary">
                {licences.length === 0 ? "No licences yet." : "No licences match that search."}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allOnPageSelected}
                          indeterminate={!allOnPageSelected && pageRows.some((row) => selectedIds.has(row.id))}
                          onChange={togglePage}
                          inputProps={{ "aria-label": "Select all on this page" }}
                        />
                      </TableCell>
                      <SortableHeader column="customer" label="Customer" sort={sort} dir={dir} onSort={toggleSort} />
                      {seesResellers ? (
                        <SortableHeader column="reseller" label="Reseller" sort={sort} dir={dir} onSort={toggleSort} />
                      ) : null}
                      <SortableHeader column="ports" label="Ports" sort={sort} dir={dir} onSort={toggleSort} />
                      <SortableHeader column="expires" label="Expires" sort={sort} dir={dir} onSort={toggleSort} />
                      <SortableHeader column="term" label="Term" sort={sort} dir={dir} onSort={toggleSort} />
                      <SortableHeader column="workflow" label="Workflow" sort={sort} dir={dir} onSort={toggleSort} />
                      <SortableHeader column="file" label="File" sort={sort} dir={dir} onSort={toggleSort} align="right" />
                      <TableCell align="right" sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((licence) => (
                      <TableRow
                        key={licence.id}
                        hover
                        selected={selectedIds.has(licence.id)}
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/crm/licences/${licence.id}`)}
                      >
                        <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(licence.id)}
                            onChange={() => toggleRow(licence)}
                            inputProps={{ "aria-label": `Select ${licence.customerName} ${licence.label}` }}
                          />
                        </TableCell>
                        <TableCell>
                          {licence.customerName}
                          {licence.label ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {licence.label}
                            </Typography>
                          ) : null}
                        </TableCell>
                        {/* Behind the reseller wall: no key from crm-api, no cell here. */}
                        <ResellerOnly>
                          <TableCell>{resellerLabel(licence)}</TableCell>
                        </ResellerOnly>
                        <TableCell>{ports(licence)}</TableCell>
                        <TableCell>{expiryLabel(licence)}</TableCell>
                        <TableCell>
                          <LicenceStatusChip licence={licence} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <LicenceWorkflowChip licence={licence} />
                            <LicenceRenewalChip licence={licence} />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {licence.currentIssue ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy === licence.id}
                              // The row opens the builder; taking the file must not also navigate.
                              onClick={(event) => {
                                event.stopPropagation()
                                void download(licence)
                              }}
                            >
                              Download
                            </Button>
                          ) : licence.approval.status === "pending" && canApproveLicences() ? (
                            // Where the file WILL be once a super admin says yes. Green, and
                            // only in the command center: resellers ask, they never answer.
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={busy === licence.id}
                              onClick={(event) => {
                                event.stopPropagation()
                                setApproving([licence])
                              }}
                            >
                              Approve
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            sx={{ width: 32, height: 32 }}
                            aria-label="Open menu"
                            onClick={(event) => {
                              event.stopPropagation()
                              setMenu({ el: event.currentTarget, id: licence.id })
                            }}
                          >
                            <MoreHorizontal size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  page={page}
                  perPage={PER_PAGE}
                  totalCount={visible.length}
                  onPageChange={setPage}
                  entityNoun="licences"
                  hideIfSinglePage
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* ONE menu for whichever row opened it — the CRM's three-dots convention. */}
        <Menu
          anchorEl={menu?.el ?? null}
          open={menu !== null}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
          <MenuItem
            onClick={() => {
              setMenu(null)
              if (menuRow) navigate(`/crm/licences/${menuRow.id}`)
            }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
            Open
          </MenuItem>
          {menuRow?.currentIssue ? (
            <MenuItem
              onClick={() => {
                setMenu(null)
                if (menuRow) void download(menuRow)
              }}
            >
              <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
              Download file
            </MenuItem>
          ) : null}
          {menuRow?.approval.status === "pending" && canApproveLicences() ? (
            <MenuItem
              onClick={() => {
                setMenu(null)
                if (menuRow) setApproving([menuRow])
              }}
              sx={{ color: "success.main" }}
            >
              <Box sx={{ mr: 1, display: "flex" }}><CheckCircle2 size={16} /></Box>
              Approve
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={() => {
              setMenu(null)
              if (menuRow) navigate(`/crm/licences/new?from=${menuRow.id}`)
            }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><Copy size={16} /></Box>
            Duplicate
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setMenu(null)
              if (menuRow) setDeleting([menuRow])
            }}
            sx={{ color: "error.main" }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
            Delete
          </MenuItem>
        </Menu>

        <BulkDeleteDialog
          isOpen={deleting.length > 0}
          onClose={() => setDeleting([])}
          items={deleting}
          entityLabel={{ singular: "Licence", plural: "Licences" }}
          cascadingWarning="A generated file, its download history and its expiry task go with the licence. A file the customer already installed keeps working on the appliance."
          renderItem={(item) => `${item.customerName} — ${item.label || item.hardwareAddress}`}
          onConfirm={remove}
        />
        <ConfirmDialog
          isOpen={approving.length > 0}
          onClose={() => setApproving([])}
          title={approving.length > 1 ? `Approve and generate ${approving.length} licences?` : "Approve and generate this licence?"}
          description={approving.map(approvalSummary).join("\n\n")}
          confirmLabel="Approve and generate"
          onConfirm={() => approve()}
        />
      </Box>
    </PagePermissionGuard>
  )
}
